import { z } from 'zod/v4';

import {
    animationPluginShapeLegacy,
    extendedPositionPluginShapeLegacy,
    selectedFileSchemaLegacy,
    visibilityPluginShapeLegacy,
} from '../../../common/schemas/legacy';

/**
 * Video source kinds, mirroring `EVideoTypes` from the host app.
 * Kept as an inline string-enum here to keep this package free of app-level aliases.
 */
export const videoTypeSchema = z.enum(['youtube', 'vimeo', 'fileManager']);

/**
 * Horizontal alignment of the video inside its container, mirroring `EHorizontalPosition`.
 * Used by the **default** variant only — the zero variant pixel-positions the widget
 * via the extended-position group instead.
 */
export const horizontalPositionSchema = z.enum(['L', 'M', 'R']);

/**
 * Custom-preview sizing strategy, mirroring `ESizeOptions`.
 */
export const previewSizeSchema = z.enum(['auto', 'cover', 'contain']);

/**
 * Play-button size presets, mirroring `ESize` from the host app (`S`/`M`/`L`).
 * Kept as inline string literals here so the package stays free of app-level
 * aliases. Each preset maps to a concrete pixel box in the view layer — see
 * `VIDEO_PLAY_BUTTON_SIZE_PX` in the host widget model (36 / 48 / 60 px).
 */
export const videoPlayButtonSizeSchema = z.enum(['S', 'M', 'L']);

/**
 * Customisation for the video's play/launch button overlay: the size preset
 * plus idle and hover colours for both the button background and the icon.
 * Shared verbatim by both placement variants (default + zero) — it lives on
 * the common data base below. Colours are stored as raw CSS colour strings
 * (hex by default); the factory seeds the product defaults.
 */
export const videoPlayButtonSchema = z.object({
    size: videoPlayButtonSizeSchema,
    backgroundColor: z.string(),
    iconColor: z.string(),
    hoverBackgroundColor: z.string(),
    hoverIconColor: z.string(),
});

/** Size preset for the video play button (`'S' | 'M' | 'L'`). */
export type VideoPlayButtonSize = z.infer<typeof videoPlayButtonSizeSchema>;

/** Full play-button customisation object stored on the video data. */
export type VideoPlayButton = z.infer<typeof videoPlayButtonSchema>;

/**
 * Pattern checks for the two URL-based video sources. Mirrored from
 * `@common/constants/regexp.const` (`REG_EXP.YOUTUBE_LINKS`, `REG_EXP.VIMEO_LINKS`).
 * Kept here to keep the package free of `@common` imports — when the host changes
 * the patterns, update both sides.
 */
const YOUTUBE_URL_PATTERN =
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|v\/|shorts\/|live\/)|youtu\.be\/)([^?&\s]+)/;
const VIMEO_URL_PATTERN =
    /(http|https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|)(\d+)(?:|\/\?)/;

/**
 * Error codes surfaced by {@link validateVideoUrl}; stable strings so the host
 * app can map them to translation keys without re-stating the rules.
 */
export type VideoUrlError = 'required' | 'invalid';

/**
 * The single source of truth for "is this URL valid for this video type".
 *
 *  - `fileManager` videos don't require a URL at all (the source is a file pick).
 *  - `youtube` / `vimeo` videos: an empty URL is a valid draft state on disk;
 *    only a non-empty URL that doesn't match the pattern is `'invalid'`.
 *  - Pass `{ strict: true }` to additionally treat empty as `'required'` — the
 *    editor (where the user is actively filling the form) uses strict; the
 *    persistence schema uses lenient so freshly-created draft videos parse OK.
 *
 * Both the zod schema (`.superRefine` below) and the host app's control component
 * delegate here so the rules stay in lockstep.
 */
export const validateVideoUrl = (
    videoType: z.infer<typeof videoTypeSchema>,
    videoUrl: string | null | undefined,
    options?: { strict?: boolean }
): VideoUrlError | null => {
    if (videoType === 'fileManager') return null;
    if (!videoUrl) return options?.strict ? 'required' : null;
    const pattern = videoType === 'youtube' ? YOUTUBE_URL_PATTERN : VIMEO_URL_PATTERN;
    return pattern.test(videoUrl) ? null : 'invalid';
};

/**
 * Unrefined base of the **default-placement** data schema — the URL/file
 * selection and preview metadata plus the default variant's
 * `horizontalPosition`/`maxWidth` layout shorthand. The zero variant derives
 * from this base via `.omit()`/`.extend()`.
 *
 * Kept separate from the exported {@link videoLegacyDefaultDataSchema}
 * because zod v4 forbids `.omit()`/`.extend()` on schemas that already carry
 * refinements — so the `.superRefine(refineVideoUrl)` step is attached to
 * each exported variant schema instead. That also keeps the issue path
 * rooted at `data.videoUrl` in both cases — where the host-app control
 * reads errors from.
 */
const videoLegacyDefaultDataBaseSchema = z.object({
    videoType: videoTypeSchema,
    videoUrl: z.string(),
    videoFile: selectedFileSchemaLegacy.nullable().optional(),
    previewUrl: z.string(),
    customPreviewFile: selectedFileSchemaLegacy.nullable().optional(),
    customPreviewUrl: z.string().optional(),
    customPreviewSize: previewSizeSchema.optional(),
    placeholder: z.string(),
    isShorts: z.boolean().optional(),
    playButton: videoPlayButtonSchema,
    horizontalPosition: horizontalPositionSchema.optional(),
    maxWidth: z.number().nullable(),
});

const refineVideoUrl: (
    data: { videoType: z.infer<typeof videoTypeSchema>; videoUrl: string },
    ctx: z.RefinementCtx
) => void = (data, ctx) => {
    const error = validateVideoUrl(data.videoType, data.videoUrl);
    if (error) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['videoUrl'],
            params: { videoUrlError: error },
            message: error,
        });
    }
};

/**
 * Data shape for the **default-placement** video variant — the legacy "drop
 * into a normal column" form. Uses the simplified `horizontalPosition` +
 * `maxWidth` shorthand for layout.
 */
export const videoLegacyDefaultDataSchema = videoLegacyDefaultDataBaseSchema.superRefine(refineVideoUrl);

/**
 * Data shape for the **zero-block-placement** video variant — derived from
 * the default base. Replaces the default layout shorthand
 * (`horizontalPosition`/`maxWidth`) with the full extended-position grid
 * (per-device width/height/x/y/rotate/zIndex), adds the three visibility
 * booleans the zero-block editor exposes, includes the animation settings
 * group, and pins `isSynchronizeStyle: false` (zero-block widgets are always
 * per-device).
 *
 * Mirrors the FormGroup produced by `getZeroVideoWidgetData` in the host app
 * (see `text` and `button` widgets for the matching pattern on form-based
 * widgets).
 */
export const videoLegacyZeroDataSchema = videoLegacyDefaultDataBaseSchema
    .omit({ horizontalPosition: true, maxWidth: true })
    .extend({
        ...extendedPositionPluginShapeLegacy,
        ...visibilityPluginShapeLegacy,
        ...animationPluginShapeLegacy,
        isSynchronizeStyle: z.literal(false),
    })
    .superRefine(refineVideoUrl);
